import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { mixpanelService, ConsentStatus } from '../services/mixpanel';

interface ConsentBannerProps {
  onConsentChange?: (status: ConsentStatus) => void;
}

const BannerContainer = styled.div<{ show: boolean }>`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--color-surface);
  border-top: 1px solid var(--color-border);
  box-shadow: var(--shadow-lg);
  padding: var(--gap-lg);
  z-index: 1000;
  transform: ${props => props.show ? 'translateY(0)' : 'translateY(100%)'};
  transition: transform 0.3s ease-in-out;
  
  @media (max-width: 768px) {
    padding: var(--gap-md);
  }
`;

const BannerContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: var(--gap-lg);
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: var(--gap-md);
    text-align: center;
  }
`;

const BannerText = styled.div`
  flex: 1;
  color: var(--color-text);
  font-size: var(--font-size-sm);
  line-height: 1.5;
  
  @media (max-width: 768px) {
    font-size: var(--font-size-xs);
  }
`;

const BannerTitle = styled.h3`
  margin: 0 0 4px 0;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  color: var(--color-text);
`;

const BannerDescription = styled.p`
  margin: 0;
  color: var(--color-text-secondary);
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: var(--gap-md);
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all 0.2s;
  min-height: 44px; /* Accessibility touch target */
  
  ${props => props.variant === 'primary' ? `
    background: var(--color-primary);
    color: white;
    border: 1px solid var(--color-primary);
    
    &:hover {
      background: var(--color-primary-hover, var(--color-primary));
      transform: translateY(-1px);
      box-shadow: var(--shadow-md);
    }
  ` : `
    background: var(--color-surface);
    color: var(--color-text);
    border: 1px solid var(--color-border);
    
    &:hover {
      background: var(--color-surface-hover);
      border-color: var(--color-primary);
    }
  `}
  
  &:active {
    transform: scale(0.98);
  }
  
  @media (max-width: 768px) {
    padding: 16px 24px;
    min-height: 56px;
    flex: 1;
  }
`;

const DNTNotice = styled.div`
  background: rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: var(--radius-md);
  padding: var(--gap-md);
  margin-top: var(--gap-md);
  color: var(--color-text);
  font-size: var(--font-size-xs);
  
  @media (max-width: 768px) {
    margin-top: var(--gap-sm);
  }
`;

const PrivacyLink = styled.a`
  color: var(--color-primary);
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

export const ConsentBanner: React.FC<ConsentBannerProps> = ({ onConsentChange }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [isDNTEnabled, setIsDNTEnabled] = useState(false);

  useEffect(() => {
    // Check if we need to show the banner
    const checkConsent = () => {
      // Check DNT status
      const dntEnabled = (
        navigator.doNotTrack === '1' ||
        navigator.doNotTrack === 'yes' ||
        // @ts-ignore
        navigator.msDoNotTrack === '1' ||
        // @ts-ignore
        window.doNotTrack === '1'
      );
      
      setIsDNTEnabled(dntEnabled);
      
      // Only show banner if no DNT and no consent yet
      const currentConsent = mixpanelService.getConsentStatus();
      const shouldShow = !dntEnabled && !currentConsent;
      
      setShowBanner(shouldShow);
    };

    // Initial check
    checkConsent();
    
    // Listen for visibility changes (in case DNT changes)
    document.addEventListener('visibilitychange', checkConsent);
    
    return () => {
      document.removeEventListener('visibilitychange', checkConsent);
    };
  }, []);

  const handleAccept = () => {
    mixpanelService.grantConsent();
    setShowBanner(false);
    onConsentChange?.('granted');
    
    console.log('[ConsentBanner] User accepted analytics');
  };

  const handleDecline = () => {
    mixpanelService.denyConsent();
    setShowBanner(false);
    onConsentChange?.('denied');
    
    console.log('[ConsentBanner] User declined analytics');
  };

  // Don't render anything if banner shouldn't show
  if (!showBanner) {
    return null;
  }

  return (
    <BannerContainer show={showBanner}>
      <BannerContent>
        <BannerText>
          <BannerTitle>We respect your privacy</BannerTitle>
          <BannerDescription>
            We use analytics to understand how you use Lore Spirals and improve your experience. 
            Your data is never sold or shared with third parties.{' '}
            <PrivacyLink href="#" onClick={(e) => e.preventDefault()}>
              Learn more about our privacy practices
            </PrivacyLink>
          </BannerDescription>
          
          {isDNTEnabled && (
            <DNTNotice>
              📍 We've detected that you have "Do Not Track" enabled in your browser. 
              We respect this setting and won't track your activity regardless of your choice below.
            </DNTNotice>
          )}
        </BannerText>
        
        <ButtonGroup>
          <Button variant="secondary" onClick={handleDecline}>
            Decline
          </Button>
          <Button variant="primary" onClick={handleAccept}>
            Accept Analytics
          </Button>
        </ButtonGroup>
      </BannerContent>
    </BannerContainer>
  );
};